// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0033-dashboard-impersonation.md
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { UserRound } from 'lucide-react';

import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { useAuth, stopImpersonation, type CurrentUser } from '@/lib/auth';

interface ImpersonationBannerProps {
  user: CurrentUser & { impersonating: NonNullable<CurrentUser['impersonating']> };
}

/**
 * Persistent, non-dismissable banner shown on every dashboard route while an
 * org_admin is impersonating another user (ADR-0033). It names the effective
 * user (whom the dashboard is rendering as) and the real operator, and the only
 * way to clear it is **Stop impersonating** — there is no close affordance.
 * High-contrast amber so it reads as an active, unusual mode at a glance.
 */
export function ImpersonationBanner({ user }: ImpersonationBannerProps): React.JSX.Element {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pending, setPending] = React.useState(false);

  const onStop = async (): Promise<void> => {
    if (pending) return;
    setPending(true);
    try {
      const res = await stopImpersonation();
      if (!res.ok) {
        setPending(false);
        return;
      }
      // Drop the impersonated session's cached reads, re-resolve identity as the
      // operator, and land on the catalog so nav/route guards match immediately.
      queryClient.clear();
      await refresh();
      navigate('/apps');
    } catch {
      setPending(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Impersonation active"
      data-testid="impersonation-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[hsl(var(--status-amber))] bg-[hsl(var(--status-amber)/0.15)] px-4 py-2 pt-[calc(0.5rem+env(safe-area-inset-top))] md:px-6"
    >
      <UserRound className="size-4 shrink-0 text-[hsl(var(--status-amber-text))]" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <span className="font-semibold text-foreground">Viewing as</span>
        <span className="min-w-0 truncate text-foreground">{user.email}</span>
        <StatusBadge variant="inactive">{user.role}</StatusBadge>
        <span className="text-muted-foreground">
          · you are{' '}
          <span className="text-foreground">
            {user.impersonating.operator_email ?? 'the operator'}
          </span>
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="h-11 shrink-0 md:h-9"
        onClick={() => void onStop()}
        disabled={pending}
        data-testid="stop-impersonating"
      >
        {pending ? 'Stopping…' : 'Stop impersonating'}
      </Button>
    </div>
  );
}
